import React from 'react';
import { Link } from 'react-router-dom';
import type {
  EntityDetailChildItem,
  EntityDetailRelationship,
  EntityDetailStateHistoryEntry,
  EntityDetailStateMachine,
} from '../../../types/kryspaceEntityDetail';
import { companyArtifactsModelPath } from '../../../utils/companyArtifactRoutes';
import { formatArtifactTypeRefDisplay } from '../../../utils/artifactTypeRefDisplay';
import styles from '../ControlBridge.module.css';

function formatEntityDetailFieldValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function isStructuredFieldValue(value: unknown): boolean {
  return value != null && typeof value === 'object';
}

export function EntityDetailStateHistoryList({
  entries,
}: {
  entries: EntityDetailStateHistoryEntry[];
}) {
  if (entries.length === 0) return null;

  return (
    <section className={styles.entityDetailSection} aria-label="Transition history">
      <h3 className={styles.entityDetailSectionTitle}>Transition history</h3>
      <ol className={styles.entityDetailStateHistoryList}>
        {entries.map((entry, index) => (
          <li key={`${entry.state}-${entry.enteredAt ?? index}`} className={styles.entityDetailStateHistoryRow}>
            <span className={styles.mcStatusPill}>{entry.state}</span>
            <span className={styles.entityDetailStateHistoryMeta}>
              {entry.enteredAt ? `from ${entry.enteredAt}` : 'entered'}
              {entry.transitionMethod ? ` · ${entry.transitionMethod}` : ''}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function EntityDetailExecutionSummary({
  itemLabel,
  actionLabel,
  contextLabel,
}: {
  itemLabel: string;
  actionLabel: string;
  contextLabel: string;
}) {
  return (
    <section className={styles.entityDetailSection} aria-label="Execution summary">
      <h3 className={styles.entityDetailSectionTitle}>Execution summary</h3>
      <dl className={styles.entityDetailFieldList}>
        <div className={styles.entityDetailFieldRow}>
          <dt>Item</dt>
          <dd>{itemLabel}</dd>
        </div>
        <div className={styles.entityDetailFieldRow}>
          <dt>Action</dt>
          <dd>{actionLabel}</dd>
        </div>
        <div className={styles.entityDetailFieldRow}>
          <dt>Context</dt>
          <dd>{contextLabel}</dd>
        </div>
      </dl>
    </section>
  );
}

export function EntityDetailStateMachineSummary({
  stateMachine,
}: {
  stateMachine: EntityDetailStateMachine;
}) {
  const { states, currentIndex } = stateMachine;
  if (states.length === 0) return null;

  return (
    <section className={styles.entityDetailSection} aria-label="State machine">
      <h3 className={styles.entityDetailSectionTitle}>State machine</h3>
      <ol className={styles.entityDetailStateMachine}>
        {states.map((state, index) => (
          <li
            key={state}
            className={
              index === currentIndex ? styles.entityDetailStateMachineCurrent : undefined
            }
          >
            {state}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function EntityDetailChildItemsList({
  items,
  onSelectItem,
}: {
  items: EntityDetailChildItem[];
  onSelectItem?: (itemId: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <section className={styles.entityDetailSection} aria-label="Child artifact items">
      <h3 className={styles.entityDetailSectionTitle}>Child items</h3>
      <ul className={styles.entityDetailChildList}>
        {items.map((item) => (
          <li key={item.itemId} className={styles.entityDetailChildRow}>
            <div className={styles.entityDetailChildMain}>
              <span className={styles.entityDetailChildName}>{item.name}</span>
              <span className={styles.mcStatusPill}>{item.currentState}</span>
              {item.eligible ? (
                <span className={styles.entityDetailChildEligible}>eligible</span>
              ) : null}
            </div>
            {onSelectItem ? (
              <button
                type="button"
                className={styles.entityDetailChildOpen}
                onClick={() => onSelectItem(item.itemId)}
                aria-label={`Open ${item.name}`}
              >
                →
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function EntityDetailRelationshipsList({
  relationships,
}: {
  relationships: EntityDetailRelationship[];
}) {
  if (relationships.length === 0) return null;

  return (
    <section className={styles.entityDetailSection} aria-label="Relationships">
      <h3 className={styles.entityDetailSectionTitle}>Relationships</h3>
      <ul className={styles.entityDetailRelationshipList}>
        {relationships.map((rel, index) => (
          <li key={`${rel.type}-${rel.targetItemId}-${index}`} className={styles.entityDetailRelationshipRow}>
            <span className={styles.entityDetailRelationshipType}>{rel.type}</span>
            <span className={styles.entityDetailRelationshipTarget}>
              {rel.targetLabel ?? rel.targetItemId}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function EntityDetailArtifactTreeLink({
  artifactInstanceId,
  workspaceSlug,
}: {
  artifactInstanceId: string;
  workspaceSlug?: string;
}) {
  const to = workspaceSlug?.trim()
    ? `${companyArtifactsModelPath(workspaceSlug.trim())}?highlight=${encodeURIComponent(artifactInstanceId)}`
    : `/artifacts/artifact-tree?artifact=${encodeURIComponent(artifactInstanceId)}`;
  return (
    <Link
      className={styles.entityDetailTreeLink}
      to={to}
      target="_blank"
      rel="noopener noreferrer"
    >
      Open in company artifact tree ↗
    </Link>
  );
}

export function EntityDetailChildCompanyArtifactsList({
  items,
  onOpenArtifactDetail,
  sectionTitle = 'Child company artifacts',
}: {
  items: Array<{
    artifactInstanceId: string;
    name: string;
    artifactTypeRef: string;
    currentState: string;
  }>;
  onOpenArtifactDetail?: (artifactInstanceId: string) => void;
  sectionTitle?: string;
}) {
  if (items.length === 0) {
    if (sectionTitle === 'Eligible work') {
      return (
        <section className={styles.entityDetailSection} aria-label={sectionTitle}>
          <h3 className={styles.entityDetailSectionTitle}>{sectionTitle}</h3>
          <p className={styles.entityDetailChildMeta}>No eligible work in this scope.</p>
        </section>
      );
    }
    return null;
  }

  return (
    <section className={styles.entityDetailSection} aria-label={sectionTitle}>
      <h3 className={styles.entityDetailSectionTitle}>{sectionTitle}</h3>
      <ul className={styles.entityDetailChildList}>
        {items.map((item) => (
          <li key={item.artifactInstanceId} className={styles.entityDetailChildRow}>
            <div>
              <div className={styles.entityDetailChildName}>{item.name}</div>
              <div className={styles.entityDetailChildMeta}>
                {formatArtifactTypeRefDisplay(item.artifactTypeRef)}
                {item.currentState ? ` · ${item.currentState}` : ''}
              </div>
            </div>
            {onOpenArtifactDetail ? (
              <button
                type="button"
                className={styles.entityDetailChildOpen}
                onClick={() => onOpenArtifactDetail(item.artifactInstanceId)}
                aria-label={`Open ${item.name}`}
              >
                →
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function EntityDetailReferencedItemSection({
  referencedItem,
  onOpenItemDetail,
}: {
  referencedItem: import('../../../types/kryspaceEntityDetail').ArtifactItemDetailPreview;
  onOpenItemDetail?: (itemId: string) => void;
}) {
  const coreFields = (referencedItem.fields ?? []).filter(
    (f) => f.tier !== 'mechanical' && (f.tier === 'core' || !f.tier)
  );

  return (
    <section className={styles.entityDetailSection} aria-label="Referenced subject item">
      <h3 className={styles.entityDetailSectionTitle}>Referenced item</h3>
      <div className={styles.entityDetailReferencedHeader}>
        <div>
          <div className={styles.entityDetailReferencedName}>{referencedItem.name}</div>
          <div className={styles.entityDetailReferencedMeta}>
            {formatArtifactTypeRefDisplay(referencedItem.artifactTypeRef)} · {referencedItem.itemId}
          </div>
        </div>
        <span className={styles.mcStatusPill}>{referencedItem.currentState}</span>
      </div>
      {referencedItem.executionSummary?.actionLabel ? (
        <p className={styles.entityDetailReferencedAction}>
          {referencedItem.executionSummary.actionLabel}
        </p>
      ) : null}
      {coreFields.length > 0 ? (
        <dl className={styles.entityDetailFieldList}>
          {coreFields.slice(0, 6).map((field) => (
            <div key={field.key} className={styles.entityDetailFieldRow}>
              <dt>{field.label}</dt>
              <dd
                className={
                  isStructuredFieldValue(field.value) ? styles.entityDetailFieldJson : undefined
                }
              >
                {formatEntityDetailFieldValue(field.value)}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {onOpenItemDetail ? (
        <button
          type="button"
          className={styles.entityDetailReferencedOpen}
          onClick={() => onOpenItemDetail(referencedItem.itemId)}
        >
          Open subject detail →
        </button>
      ) : null}
    </section>
  );
}

export function EntityDetailLoadingState() {
  return (
    <p className={styles.entityDetailStatus} data-testid="entity-detail-loading">
      Loading detail…
    </p>
  );
}

export function EntityDetailErrorState({ message }: { message: string }) {
  return (
    <p className={styles.entityDetailStatusError} data-testid="entity-detail-error" role="alert">
      {message}
    </p>
  );
}
